import { Box } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ReactFlowState, useStore } from 'reactflow'
import { NODE_HEADER_GAP } from '../../helpers/yard-view.utils'
import { NodeRotation } from './NodeRotatation'

const zoomSelector = (state: ReactFlowState) => state.transform[2]

interface Props {
  rotation: number
  enabled: boolean
  header?: React.ReactNode
  minHeaderSpacing?: number
  parentDeterminesSize?: boolean
  children: React.ReactNode
  onRotationEnd: (rotation: number) => void
}
export const RotatableNodeWrapper = ({
  rotation,
  enabled,
  header,
  minHeaderSpacing = NODE_HEADER_GAP,
  parentDeterminesSize = false,
  children,
  onRotationEnd,
}: Props) => {
  const zoom = useStore(zoomSelector)
  const headerScale = Math.min(Math.max(0.75 / zoom, 1), 3)

  const [localRotation, setLocalRotation] = useState(rotation)
  const rotationRef = useRef(rotation)

  useEffect(() => {
    setLocalRotation(rotation)
    rotationRef.current = rotation
  }, [rotation])

  const handleRotate = useCallback((angle: number) => {
    rotationRef.current = angle
    setLocalRotation(angle)
  }, [])

  const handleRotateEnd = useCallback(() => {
    onRotationEnd(rotationRef.current)
  }, [onRotationEnd])

  const isFlipped = localRotation > 90 || localRotation < -90

  return (
    <Box
      sx={{
        position: 'absolute',
        visibility: 'visible',
        display: 'inline-block',
        width: '100%',
        height: '100%',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: parentDeterminesSize ? '100%' : 'fit-content',
          height: parentDeterminesSize ? '100%' : 'fit-content',
          transform: `rotate(${localRotation}deg)`,
          transformOrigin: 'center',
        }}
      >
        {header && (
          <Box
            className='nodrag'
            sx={{
              position: 'absolute',
              left: '50%',
              zIndex: 20,
              pointerEvents: 'auto',
              ...(isFlipped
                ? {
                    transform: `translateX(-50%) rotate(180deg) scale(${headerScale})`,
                    transformOrigin: 'center top',
                  }
                : {
                    top: -minHeaderSpacing,
                    transform: `translateX(-50%) scale(${headerScale})`,
                    transformOrigin: 'center bottom',
                  }),
            }}
          >
            {header}
          </Box>
        )}

        <Box sx={{ width: '100%', height: '100%' }}>
          {children}
          {enabled && (
            <NodeRotation
              rotation={localRotation}
              zoom={zoom}
              onRotate={handleRotate}
              onRotationEnd={handleRotateEnd}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
